import type { SaveShopProductRequest, ShopProduct } from "../../api/shop";

/**
 * Why the list's Active switch can't save this product as it is, or null. A product with no category (an old one
 * not yet filed) needs one first, and only the editor can set it.
 */
export const activeToggleProblem = (product: Pick<ShopProduct, "categoryId" | "subcategoryId">): string | null =>
  product.subcategoryId || product.categoryId ? null : "Choose a category in Edit first";

/**
 * A product as its save request, e.g. to flip isActive from the list. Each SKU is sent by its combination
 * (its style's values + its size) with the stock the admin saw as expectedStock, so the server refuses the save
 * if orders moved the stock since.
 */
export const toSaveShopProductRequest = (
  product: ShopProduct,
  overrides: Partial<SaveShopProductRequest> = {}
): SaveShopProductRequest => {
  const valuesByStyle = new Map(product.styles.map((s) => [s.id, s.optionValueIds] as const));
  return {
    name: product.name,
    description: product.description,
    categoryId: product.subcategoryId ?? product.categoryId ?? null,
    sportIds: product.sportIds,
    price: product.price,
    code: product.code,
    trackStock: product.trackStock,
    isActive: product.isActive,
    optionValueIds: product.optionTypes.flatMap((t) => t.values.map((v) => v.id)),
    sizeIds: product.sizes.map((s) => s.id),
    styles: product.styles.map((s) => ({ optionValueIds: s.optionValueIds, imageUrls: s.imageUrls })),
    skus: product.variants.map((v) => ({
      optionValueIds: valuesByStyle.get(v.styleId) ?? [],
      sizeId: v.sizeId,
      sku: v.sku,
      stock: v.stock,
      expectedStock: v.stock,
      priceOverride: v.priceOverride,
      isEnabled: v.isEnabled,
    })),
    ...overrides,
  };
};
